import {
  ICollision,
  CollideHandler,
} from '../../actions/collisions/ICollision';
import { InvalidEffect } from '../../actions/effects/InvalidEffect';
import { CollideEvent, CollideId, isPowerCollider } from '../../defs/collide';
import { despawnBlockEntity } from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { FlowerFidgetBlockEntity } from './FlowerFidgetBlockEntity';
import {
  flowerFidgetCheckStateComplete,
  FlowerFidgetColor,
} from './FlowerFidgetBlock';
import { app } from '../../app';
import { ColorId, PowerBlockType } from '../../defs/block';
import { randomShuffle } from '../../../odie';
import { FlowerFidgetBlockEffect } from './FlowerFidgetBlockEffect';

export class FlowerFidgetBlockCollision implements ICollision {
  private readonly scene: GameScene;
  private readonly entity: FlowerFidgetBlockEntity;
  private readonly resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this.resolveTap,
    block: this.resolveBlock,
    attack: this.resolveAttack,
  };

  constructor(scene: GameScene, entity: FlowerFidgetBlockEntity) {
    this.scene = scene;
    this.entity = entity;
  }

  public async resolve(event: CollideEvent): Promise<boolean> {
    return this.resolvers[event.id]?.call(this, event) ?? false;
  }

  private async resolveTap() {
    await new InvalidEffect(this.entity).execute();
    return false;
  }

  private async resolveBlock(event: CollideEvent) {
    await this.blockHit(event);
    return true;
  }

  private async resolveAttack(event: CollideEvent) {
    await this.powerHit(event);
    return true;
  }

  private async blockHit(event: CollideEvent) {
    if (event.collider?.type !== 'block') return;
    const block = event.collider?.entity;
    if (!block) return;
    const type = block.c.block.props.type;
    if (type !== 'basic') return;
    const color = block.c.block.props.color;
    await this.hit(color);
  }

  private async powerHit(event: CollideEvent) {
    const type = event.collider?.type as PowerBlockType;
    if (!isPowerCollider(type)) return;

    // Retrieve the updated state
    const state = this.entity.c.blockFlowerFidget.getState();

    // Find out which colors are remaining
    const remainingColors = Object.keys(state).filter(
      (color) => !state[color as FlowerFidgetColor],
    ) as ColorId[];

    // Just return if no colors are remaining - should not happen
    if (!remainingColors.length) return;

    // Power hit should hit a random color
    const shuffledColors = randomShuffle(remainingColors);

    // Hit the first color from the shuffled list
    await this.hit(shuffledColors[0]);
  }

  private async hit(color: ColorId) {
    // Hit the block with the color
    const hitEffective = this.entity.c.blockFlowerFidget.hitColor(color);
    const state = this.entity.c.blockFlowerFidget.getState();
    const complete = flowerFidgetCheckStateComplete(state);

    // If completed, play the fly animation and despawn the block
    if (complete) {
      const effect = new FlowerFidgetBlockEffect(this.scene);
      void effect.playFlyAnimation(this.entity);
      despawnBlockEntity(this.scene, this.entity);
      return;
    }

    // If hit was effective, play the hit animation
    if (hitEffective) {
      const effect = new FlowerFidgetBlockEffect(this.scene);
      void effect.playHitAnimation(this.entity);
    }
  }
}
